import html2canvas from "html2canvas";

const MODAL_CAPTURE_ID = "modal-seguimiento-whatsapp-captura";
const waitForPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const emitProgress = (onProgress, progress, message) => {
  if (typeof onProgress === "function") {
    onProgress({ progress, message });
  }
};

const getCaptureScale = (element) => {
  const width = element?.getBoundingClientRect?.().width || element?.offsetWidth || 1280;
  if (width > 1600) return 1;
  if (width > 1200) return 1.15;
  return Math.min(window.devicePixelRatio || 1, 1.4);
};

const createShareError = (stage, message, details = {}) => {
  const error = new Error(message);
  error.stage = stage;
  Object.assign(error, details);
  return error;
};

const safeValue = (value, fallback = "N/A") => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim();
  return normalized ? normalized : fallback;
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeValue(value);

  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export async function crearImagenDesdeElemento(
  elementId = MODAL_CAPTURE_ID,
  fileName = "seguimiento-modal.png",
  options = {}
) {
  const { onProgress } = options;
  const element = document.getElementById(elementId);

  if (!element) {
    throw createShareError(
      "capture-element",
      `No se encontro el elemento con id: ${elementId}`
    );
  }

  emitProgress(onProgress, 22, "Preparando vista para compartir...");
  await waitForPaint();

  // Espera a que los iconos e imágenes del modal terminen de cargar antes
  // de tomar la captura para evitar espacios vacíos o imágenes incompletas.
  const images = Array.from(element.querySelectorAll("img"));
  await Promise.all(
    images.map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener("load", resolve, { once: true });
        image.addEventListener("error", resolve, { once: true });
      });
    })
  );

  // Usa el tamaño visible del modal y el viewport real. Si se usa scrollWidth
  // o el ancho del elemento como windowWidth, html2canvas puede activar un
  // breakpoint distinto y generar una captura móvil o recortada.
  const bounds = element.getBoundingClientRect();
  const elementWidth = Math.ceil(bounds.width || element.offsetWidth || element.clientWidth || 1);
  const elementHeight = Math.ceil(bounds.height || element.offsetHeight || element.clientHeight || 1);
  const viewportWidth = Math.max(window.innerWidth || 0, document.documentElement.clientWidth || 0, elementWidth);
  const viewportHeight = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0, elementHeight);
  const captureScale = getCaptureScale(element);

  emitProgress(onProgress, 42, "Generando captura del modal...");
  const canvas = await html2canvas(element, {
    useCORS: true,
    allowTaint: false,
    scale: captureScale,
    backgroundColor: "#ffffff",
    logging: false,
    scrollX: 0,
    scrollY: 0,
    width: elementWidth,
    height: elementHeight,
    windowWidth: viewportWidth,
    windowHeight: viewportHeight,
    onclone: (clonedDocument) => {
      const clonedRoot = clonedDocument.getElementById(elementId);
      if (!clonedRoot) return;

      clonedRoot.style.width = `${elementWidth}px`;
      clonedRoot.style.maxWidth = "none";
      clonedRoot.style.backgroundColor = "#ffffff";

      // El filtro de iconos puede estar abierto en el modal real. Se cierra
      // únicamente en el clon para que no aparezca desplegado en la imagen.
      clonedRoot.querySelectorAll("details[open]").forEach((details) => {
        details.removeAttribute("open");
      });
      clonedRoot.querySelectorAll(".icon-filter-menu").forEach((menu) => {
        menu.style.display = "none";
      });

      const shareButtons = clonedRoot.querySelectorAll('[data-capture-share-button="true"]');
      shareButtons.forEach((button) => {
        button.disabled = false;
        button.style.pointerEvents = "auto";
        button.style.opacity = "1";
        button.style.background = "";
        button.style.borderColor = "";
        button.style.boxShadow = "0 8px 18px rgba(17, 52, 77, 0.10)";
        button.style.transform = "translateY(0)";
        button.textContent = button.getAttribute("data-capture-label") || "Compartir WhatsApp";
      });
    },
  });

  emitProgress(onProgress, 64, "Convirtiendo captura a imagen...");
  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, "image/png", 1);
  });

  if (!blob) {
    throw createShareError(
      "capture-blob",
      "No se pudo generar la imagen del modal."
    );
  }

  return new File([blob], fileName, {
    type: "image/png",
  });
}

export const soportaCompartirArchivos = (file) =>
  Boolean(
    navigator.share &&
    navigator.canShare &&
    file &&
    navigator.canShare({ files: [file] })
  );

const copiarTextoAlPortapapeles = async (text) => {
  if (!text) return false;
  if (!navigator.clipboard?.writeText) return false;

  await navigator.clipboard.writeText(text);
  return true;
};

const copiarImagenAlPortapapeles = async (file) => {
  try {
    if (!file || !navigator.clipboard?.write || typeof window.ClipboardItem === "undefined") {
      throw new Error("Clipboard API no soporta copiado de imagen.");
    }

    const blob = new Blob([await file.arrayBuffer()], { type: "image/png" });
    const item = new window.ClipboardItem({ "image/png": blob });
    await navigator.clipboard.write([item]);
    return true;
  } catch (error) {
    console.error("Error copiando imagen al portapapeles:", error);
    return false;
  }
};

export function construirTextoSeguimiento(seguimiento) {
  const texto = `
Seguimiento de contrato

Contrato: ${safeValue(seguimiento?.contrato)}
Cliente: ${safeValue(seguimiento?.cliente)}
Dispositivo: ${safeValue(seguimiento?.dispositivo)}
Ruta: ${safeValue(seguimiento?.ruta)}
Estado: ${safeValue(seguimiento?.estado)}
Ultimo reporte: ${safeValue(seguimiento?.ultimo_reporte)}
Distancia ruta: ${safeValue(seguimiento?.distancia_ruta)}
Distancia faltante: ${safeValue(seguimiento?.distancia_faltante)}

Ultimo evento:
Fecha: ${safeValue(seguimiento?.fecha)}
Lugar: ${safeValue(seguimiento?.lugar)}
Geocerca: ${safeValue(seguimiento?.geocerca)}
Bateria: ${safeValue(seguimiento?.bateria)}
GPS: ${safeValue(seguimiento?.gps)}
Velocidad: ${safeValue(seguimiento?.velocidad)}
  `.trim();
  return texto;
}

export function construirPayloadSeguimiento(contratoVar, lastReport, metrics = {}) {
  const fallbackEstado = lastReport?.Locked === 0 ? "Apertura" : "Cierre";
  const batteryValue =
    lastReport?.Battery !== null && lastReport?.Battery !== undefined
      ? `${parseFloat(lastReport.Battery).toFixed(2).replace(/\.?0+$/, "")}%`
      : "N/A";
  const velocidad =
    lastReport?.Speed !== null && lastReport?.Speed !== undefined
      ? `${lastReport.Speed} km/h`
      : "N/A";

  return {
    contrato: safeValue(contratoVar?.ContractID),
    cliente: safeValue(contratoVar?.NombreEmpresa),
    dispositivo: safeValue(contratoVar?.FKLokDeviceID),
    ruta: safeValue(contratoVar?.DescripcionRuta),
    estado: safeValue(contratoVar?.TipoReporte, fallbackEstado),
    ultimo_reporte: formatDateTime(lastReport?.PositionTime || contratoVar?.LoksysServerTime),
    distancia_ruta: safeValue(metrics?.distanciaRuta),
    distancia_faltante: safeValue(metrics?.distanciaFaltante),
    fecha: formatDateTime(lastReport?.PositionTime),
    lugar: safeValue(lastReport?.Ciudad),
    geocerca: safeValue(lastReport?.NombreGeocerca),
    bateria: batteryValue,
    gps: safeValue(lastReport?.SatelliteCount),
    velocidad,
  };
}

export async function prepararModalSeguimientoWhatsApp(seguimiento, options = {}) {
  const { onProgress, elementId = MODAL_CAPTURE_ID } = options;
  try {
    const fileName = `seguimiento-${safeValue(seguimiento?.contrato, "contrato")
      .toLowerCase()
      .replace(/[^a-z0-9-_]+/g, "-")}.png`;

    emitProgress(onProgress, 10, "Reuniendo datos para compartir...");
    const file = await crearImagenDesdeElemento(elementId, fileName, {
      onProgress,
    });
    const nativeShareSupported = soportaCompartirArchivos(file);

    emitProgress(onProgress, 100, "Captura lista para compartir.");

    return {
      ok: true,
      metodo: nativeShareSupported ? "web-share-files" : "whatsapp-web-fallback",
      nativeShareSupported,
      file,
      title: "Seguimiento de contrato",
      text: construirTextoSeguimiento(seguimiento),
    };
  } catch (error) {
    if (error?.stage) {
      throw error;
    }

    throw createShareError(
      "share-unknown",
      error?.message || "Ocurrio un error inesperado al preparar el seguimiento.",
      {
        originalError: error,
      }
    );
  }
}

export async function compartirArchivoPreparado(preparedShare, options = {}) {
  const { minOverlayMs = 0 } = options;
  const startedAt = Date.now();

  try {
    if (!preparedShare?.file) {
      throw createShareError(
        "share-not-ready",
        "La captura aun no esta preparada para compartir."
      );
    }

    if (!soportaCompartirArchivos(preparedShare.file)) {
      throw createShareError(
        "share-not-supported",
        "Este navegador o dispositivo no permite compartir archivos directamente desde la web."
      );
    }

    try {
      const shareData = {
        title: preparedShare.title || "Seguimiento de contrato",
        files: [preparedShare.file],
      };

      if (preparedShare.text) {
        shareData.text = preparedShare.text;
      }

      await navigator.share(shareData);
      const elapsedMs = Date.now() - startedAt;
      if (elapsedMs < minOverlayMs) {
        await wait(minOverlayMs - elapsedMs);
      }

      return {
        ok: true,
        metodo: "web-share-files",
      };
    } catch (error) {
      if (error?.name === "AbortError") {
        return {
          ok: false,
          cancelado: true,
        };
      }

      const isGestureError = /user gesture/i.test(error?.message || "");

      throw createShareError(
        isGestureError ? "share-native-gesture" : "share-native",
        error?.message || "El sistema no pudo abrir o completar el panel nativo de compartir.",
        {
          originalError: error,
        }
      );
    }
  } catch (error) {
    if (error?.stage) {
      throw error;
    }

    throw createShareError(
      "share-unknown",
      error?.message || "Ocurrio un error inesperado al compartir el seguimiento.",
      {
        originalError: error,
      }
    );
  }
}

export async function compartirArchivoPorWhatsAppWeb(
  preparedShare,
  options = {}
) {
  try {
    if (!preparedShare?.file) {
      throw createShareError(
        "share-not-ready",
        "La captura aun no esta preparada para compartir."
      );
    }

    /*
     * 1. Texto que queremos precargar en WhatsApp
     */
    const text = preparedShare.text || "";

    /*
     * 2. Copiar SOLAMENTE la imagen al portapapeles.
     *
     * De esta forma Ctrl + V pegará la imagen,
     * no el texto.
     */
    const copiedImage =
      await copiarImagenAlPortapapeles(
        preparedShare.file
      );

    /*
     * 3. El texto viaja por la URL.
     */
    const whatsappUrl =
      "https://web.whatsapp.com/send?text=" +
      encodeURIComponent(text);

    /*
     * IMPORTANTE:
     *
     * NO _blank
     * NO noopener
     * NO noreferrer
     *
     * Usamos un nombre fijo.
     *
     * La primera vez Cargotronics abrirá una pestaña
     * de WhatsApp.
     *
     * Después reutilizará ESA MISMA pestaña.
     */
    const openedWindow = window.open(
      whatsappUrl,
      "cargotronics-whatsapp"
    );

    if (!openedWindow) {
      throw createShareError(
        "share-whatsapp-web",
        "El navegador bloqueo WhatsApp Web."
      );
    }

    try {
      openedWindow.focus();
    } catch (error) {
      console.warn(
        "No fue posible enfocar WhatsApp Web:",
        error
      );
    }

    return {
      ok: true,
      metodo: "whatsapp-web",
      copiedImage,
      copiedText: false,
      openedWhatsappWeb: true,
    };

  } catch (error) {
    if (error?.stage) {
      throw error;
    }

    throw createShareError(
      "share-whatsapp-web",
      error?.message || "No se pudo compartir.",
      {
        originalError: error,
      }
    );
  }
}
